import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { File } from '@shared/components/file-manager/state/file.model';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import {
  GqlService,
  listClosingChecklistUpdatesQuery,
  listUserNamesWithEmailQuery,
  listUsersQuery,
  listWorkflowDetailsQuery,
  WorkflowDetailType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { TrialUserService } from '@shared/store/trial-users/trial-user.service';

import {
  QuarterCloseChecklistRowPermissionFields,
  QuarterCloseChecklistRowSections,
  QuarterCloseChecklistRowTitles,
  ShowWarningModalResponse,
} from './workflow-types';
import {
  EDCProperties,
  Workflow,
  WorkflowModel,
  WorkflowProperty,
  WorkflowStore,
} from './workflow.store';
import { EMPTY_UUID } from '../../constants/uuid';

@Injectable({ providedIn: 'root' })
export class WorkflowService {
  private authQuery = inject(AuthQuery);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private trialUserService = inject(TrialUserService);
  private workflowStore = inject(WorkflowStore);

  edcFileNumber$ = new BehaviorSubject<number>(0);
  isEDCDocumentSaved$ = new BehaviorSubject<File[]>([]);
  listUsers$ = new BehaviorSubject<listUsersQuery[]>([]);
  trialMonthClose$ = new BehaviorSubject<string>('');

  // eslint-disable-next-line max-lines-per-function
  changeLockStatus = async (shouldLock: boolean, workflow: WorkflowModel, isAssign: boolean) => {
    const isAdminUser = this.authQuery.adminUser();

    // Allow any rows to lock themselves
    if (!workflow.properties.locked) {
      await this.updateWorkflowLockStatus(shouldLock, workflow, isAdminUser, isAssign);

      return;
    }

    // Allow CloseDiscounts row to be unlocked at any time
    if (workflow.name === QuarterCloseChecklistRowTitles.CloseDiscounts) {
      await this.updateWorkflowLockStatus(shouldLock, workflow, isAdminUser, isAssign);

      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises, max-lines-per-function
    this.getWorkflowListFromStore().subscribe(async (workflowData) => {
      if (!workflowData.success || workflowData.errors.length || !workflowData.data) {
        return;
      }

      const workflowList = workflowData.data as WorkflowModel[];

      // Get CloseExpenses and CloseDiscounts workflows
      const closeExpensesWorkflow = workflowList.find(
        (checklistWorkflow) =>
          checklistWorkflow.name === QuarterCloseChecklistRowTitles.CloseExpenses
      );
      const closeDiscountsWorkflow = workflowList.find(
        (checklistWorkflow) =>
          checklistWorkflow.name === QuarterCloseChecklistRowTitles.CloseDiscounts
      );

      if (closeExpensesWorkflow && closeDiscountsWorkflow) {
        // If CloseExpenses and CloseDiscounts are unlocked, allow any unlock
        if (!closeExpensesWorkflow.properties.locked && !closeDiscountsWorkflow.properties.locked) {
          await this.updateWorkflowLockStatus(shouldLock, workflow, isAdminUser, isAssign);

          return;
        }

        // Allow CloseExpenses to unlock if CloseDiscounts is unlocked.
        if (
          workflow.name === QuarterCloseChecklistRowTitles.CloseExpenses &&
          !closeDiscountsWorkflow.properties.locked
        ) {
          await this.updateWorkflowLockStatus(shouldLock, workflow, isAdminUser, isAssign);

          return;
        }

        // Unlocking CloseExpenses will unlock CloseExpenses and CloseDiscounts
        if (
          workflow.name === QuarterCloseChecklistRowTitles.CloseExpenses &&
          closeDiscountsWorkflow.properties.locked
        ) {
          // Warn user
          const message = `Unlocking this item will automatically unlock Discounts. All checklist entries must be locked in order to close the period.`;
          const warning = await this.showWarningModal(workflow.name, message);

          if (warning.data) {
            // Unlock CloseDiscounts and CloseExpenses
            await this.updateWorkflowLockStatus(
              shouldLock,
              closeDiscountsWorkflow,
              isAdminUser,
              isAssign
            );
            await this.updateWorkflowLockStatus(shouldLock, workflow, isAdminUser, isAssign);

            return;
          }

          return;
        }

        // All other unlocks while CloseExpenses is locked, will unlock both
        if (closeExpensesWorkflow.properties.locked && !closeDiscountsWorkflow.properties.locked) {
          // Warn user
          const message = `Unlocking this item will automatically unlock Vendor Contract Accruals. All checklist entries must be locked in order to close the period.`;
          const warning = await this.showWarningModal(workflow.name, message);

          if (warning.data) {
            // Unlock CloseExpenses and workflow
            await this.updateWorkflowLockStatus(
              shouldLock,
              closeExpensesWorkflow,
              isAdminUser,
              isAssign
            );
            await this.updateWorkflowLockStatus(shouldLock, workflow, isAdminUser, isAssign);

            return;
          }

          return;
        }

        // All other unlocks while CloseExpenses and CloseDiscounts, will unlock all three
        if (closeExpensesWorkflow.properties.locked && closeDiscountsWorkflow.properties.locked) {
          // Warn user
          const message = `Unlocking this item will automatically unlock Vendor Expenses and Discounts. All checklist entries must be locked in order to close the period.`;
          const warning = await this.showWarningModal(workflow.name, message);

          if (warning.data) {
            // Unlock CloseDiscounts, CloseExpenses, and workflow
            await this.updateWorkflowLockStatus(
              shouldLock,
              closeDiscountsWorkflow,
              isAdminUser,
              isAssign
            );
            await this.updateWorkflowLockStatus(
              shouldLock,
              closeExpensesWorkflow,
              isAdminUser,
              isAssign
            );
            await this.updateWorkflowLockStatus(shouldLock, workflow, isAdminUser, isAssign);
          }
        }
      }
    });
  };

  private fetchUsersIfNeeded = ([
    listWorkflowDetailsQuery,
    listClosingChecklistUpdatesQuery,
    listUsersQuery,
    listUserNamesWithEmailQuery,
  ]: [
    GraphqlResponse<listWorkflowDetailsQuery[]>,
    GraphqlResponse<listClosingChecklistUpdatesQuery[]>,
    GraphqlResponse<listUsersQuery[]>,
    listUserNamesWithEmailQuery[],
  ]): Observable<
    [
      GraphqlResponse<listWorkflowDetailsQuery[]>,
      GraphqlResponse<listClosingChecklistUpdatesQuery[]>,
      GraphqlResponse<listUsersQuery[]>,
      listUserNamesWithEmailQuery[],
    ]
  > => {
    const allUsers$ =
      listClosingChecklistUpdatesQuery.data &&
      listClosingChecklistUpdatesQuery.data.some(
        (query) =>
          query.updated_by &&
          query.updated_by !== EMPTY_UUID &&
          listUserNamesWithEmailQuery.every((user) => user.sub !== query.updated_by)
      )
        ? this.gqlService.listUserNamesWithEmail$(true).pipe(
            map((response) => {
              return response.data || [];
            })
          )
        : of(listUserNamesWithEmailQuery);

    return allUsers$.pipe(
      switchMap((users) =>
        of([listWorkflowDetailsQuery, listClosingChecklistUpdatesQuery, listUsersQuery, users])
      )
    ) as Observable<
      [
        GraphqlResponse<listWorkflowDetailsQuery[]>,
        GraphqlResponse<listClosingChecklistUpdatesQuery[]>,
        GraphqlResponse<listUsersQuery[]>,
        listUserNamesWithEmailQuery[],
      ]
    >;
  };

  getFullNameAuthor(userId: string, users: listUserNamesWithEmailQuery[], isAdminUser: boolean) {
    const user = users.find(({ sub }) => sub === userId);

    if (!user) {
      return '';
    }

    return user.email.includes('@auxili.us') && !isAdminUser
      ? 'Auxilius Expert'
      : `${user.given_name} ${user.family_name}`;
  }

  getTrialInformation() {
    return this.mainQuery.getValue().currentOpenMonth;
  }

  getWorkflowList(workflowDate?: string) {
    return this.mainQuery.select('currentOpenMonth').pipe(
      switchMap((currentOpenMonth) => {
        const trialKey = this.mainQuery.getValue().trialKey;

        this.workflowStore.setLoading(true);

        let workflowCurrentTrialDate = '';

        if (currentOpenMonth) {
          this.trialMonthClose$.next(currentOpenMonth);

          workflowCurrentTrialDate = dayjs(currentOpenMonth).format('MMM-YYYY').toUpperCase();
        }

        return combineLatest([
          this.gqlService.listWorkflowDetails$({
            entity_id: trialKey,
            properties: JSON.stringify({
              month: workflowDate || workflowCurrentTrialDate,
            }),
            type: WorkflowDetailType.WF_MONTH_CLOSE_LOCK,
          }),
          this.gqlService.listClosingChecklistUpdates$(),
          this.trialUserService.get(),
          this.mainQuery.select('userList'),
        ]).pipe(
          switchMap(this.fetchUsersIfNeeded),
          tap(
            ([
              { data, errors: listWorkflowErrors },
              { data: workflowUpdateList, errors: listWorkflowUpdateErrors },
              { data: listActiveUsersWithPermissions, errors: listUsersQueryErrors },
              allUsersList,
            ]) => {
              if (
                data &&
                listActiveUsersWithPermissions &&
                currentOpenMonth &&
                workflowUpdateList
              ) {
                this.workflowStore.set(
                  this.transformEntities({
                    userList: allUsersList as listUserNamesWithEmailQuery[],
                    workflowList: data,
                    workflowUpdateList,
                  })
                );

                this.listUsers$.next(listActiveUsersWithPermissions || []);
              }

              if (!currentOpenMonth) {
                this.workflowStore.set([]);
              }

              const errors = listWorkflowErrors || listWorkflowUpdateErrors || listUsersQueryErrors;

              if (errors) {
                this.overlayService.error(errors);
              }

              this.workflowStore.setLoading(false);
            }
          )
        );
      })
    );
  }

  getWorkflowListFromStore(): Observable<GraphqlResponse<listWorkflowDetailsQuery[] | Workflow[]>> {
    const workflowData = Object.values(this.workflowStore.getValue().entities || {});
    const isStoreEmpty = !!workflowData.length;

    if (!isStoreEmpty) {
      return this.getWorkflowList().pipe(
        map(([workflowList]) => {
          return workflowList;
        })
      );
    }

    return of({ data: workflowData, errors: [], success: true });
  }

  showWarningModal(workflowName: string, message: string): ShowWarningModalResponse {
    let name = workflowName;

    if (name.includes('Confirm ')) {
      const finalNameIndex = 1;

      name = name.split('Confirm ')[finalNameIndex];
    }

    if (name.includes('Review ')) {
      const finalNameIndex = 1;

      name = name.split('Review ')[finalNameIndex];
    }

    const modal = this.overlayService.openPopup<{ message: string }, boolean>({
      content: ConfirmationModalComponent,
      data: {
        message,
      },
      settings: {
        header: `Unlock ${name}?`,
        primaryButton: {
          label: 'Continue',
        },
      },
    });

    return firstValueFrom(modal.afterClosed$);
  }

  private transformEntities({
    userList,
    workflowList,
    workflowUpdateList,
  }: {
    userList: listUserNamesWithEmailQuery[];
    workflowList: listWorkflowDetailsQuery[];
    workflowUpdateList: listClosingChecklistUpdatesQuery[];
  }): Workflow[] {
    const isAdminUser = this.authQuery.adminUser();

    return workflowList.map((workflow) => {
      const workflowProperties = JSON.parse(workflow.properties)?.properties as WorkflowProperty;
      const workflowName: QuarterCloseChecklistRowTitles =
        [...Object.values(QuarterCloseChecklistRowTitles)].find((title) => {
          return title === workflow.name;
        }) || QuarterCloseChecklistRowTitles.GatherContracts;

      const section =
        Object.entries(QuarterCloseChecklistRowSections).find(([key]) => {
          return key === workflowName;
        })?.[1] ||
        QuarterCloseChecklistRowSections[
          'Pending Change Orders, Purchase Orders, New Vendor Contracts, or Vendor Budgets'
        ];

      const permissionField =
        Object.entries(QuarterCloseChecklistRowPermissionFields).find(([key]) => {
          return key === workflowName;
        })?.[1] ||
        QuarterCloseChecklistRowPermissionFields[
          'Pending Change Orders, Purchase Orders, New Vendor Contracts, or Vendor Budgets'
        ];

      let { update_date, updated_by } =
        workflowUpdateList.find((w) => w.step === workflow.step_type) || {};

      if (workflowProperties.last_update_info) {
        update_date = workflowProperties.last_update_info.update_date;
        updated_by = workflowProperties.last_update_info.updated_by;
      }

      return {
        ...workflow,
        last_update_date: update_date || '',
        last_updated_by: updated_by || '',
        last_updated_by_full_name: this.getFullNameAuthor(updated_by || '', userList, isAdminUser),
        permissionField,
        properties: workflowProperties,
        section,
        updatedAuthorFullName: this.getFullNameAuthor(workflow.updated_by, userList, isAdminUser),
        workflowName,
      };
    });
  }

  async updateWorkflowLockStatus(
    locked: boolean,
    workflow: WorkflowModel,
    isAdminUser: boolean,
    isAssignProcess: boolean
  ) {
    const soft_update = isAssignProcess;
    const { data, success } = await firstValueFrom(
      this.gqlService.updateWorkflowDetail$({
        id: workflow.id,
        properties: JSON.stringify({ properties: { ...workflow.properties, locked } }),
        soft_update,
      })
    );

    if (success && data) {
      this.workflowStore.update(workflow.id, {
        properties: { ...workflow.properties, locked },
        update_date: data.update_date,
        updatedAuthorFullName: this.getFullNameAuthor(
          data.updated_by,
          this.listUsers$.getValue(),
          isAdminUser
        ),
      });
    }
  }

  async updateWorkflowPropertiesForEdcUpload(
    workflow: WorkflowModel,
    edc_properties: EDCProperties,
    isAdminUser = false
  ): Promise<boolean> {
    const { data, success } = await firstValueFrom(
      this.gqlService.updateWorkflowDetail$({
        id: workflow.id,
        properties: JSON.stringify({ properties: { ...workflow.properties, ...edc_properties } }),
        soft_update: true,
      })
    );

    if (success && data) {
      this.workflowStore.update(workflow.id, {
        properties: { ...workflow.properties, ...edc_properties },
        update_date: data.update_date,
        updatedAuthorFullName: this.getFullNameAuthor(
          data.updated_by,
          this.listUsers$.getValue(),
          isAdminUser
        ),
      });

      return true;
    }

    return false;
  }
}
